import { RAID_BRIDGE_ENDPOINT } from './raidBridgeApi.js';

export const RAID_BRIDGE_USERSCRIPT_URL = '/dororaid-bridge.user.js';
export const RAID_BRIDGE_USERSCRIPT_VERSION = '0.4.7';
export const TAMPERMONKEY_INSTALLER_URL = 'https://www.tampermonkey.net/script_installation.php?locale=ko';

const MESSAGE_TYPES = {
  ping: 'DORORAID_BRIDGE_PING',
  pong: 'DORORAID_BRIDGE_PONG',
  config: 'DORORAID_BRIDGE_CONFIG',
  configAck: 'DORORAID_BRIDGE_CONFIG_ACK',
  collect: 'DORORAID_BRIDGE_COLLECT',
  collectAck: 'DORORAID_BRIDGE_COLLECT_ACK',
  roster: 'DORORAID_BRIDGE_COLLECT_ROSTER',
  rosterAck: 'DORORAID_BRIDGE_COLLECT_ROSTER_ACK',
  autoStatus: 'DORORAID_BRIDGE_AUTO_STATUS',
  autoStatusAck: 'DORORAID_BRIDGE_AUTO_STATUS_ACK',
  autoConfig: 'DORORAID_BRIDGE_AUTO_CONFIG',
  autoConfigAck: 'DORORAID_BRIDGE_AUTO_CONFIG_ACK',
};
const REQUEST_KEY = 'DORORAID_BRIDGE_REQUEST_V3';
const RESPONSE_PREFIX = 'DORORAID_BRIDGE_RESPONSE_V3_';
const TOKEN_PATTERN = /drb_[0-9a-f]{64}/i;
const BRIDGE_RESPONSE_POLL_MS = 50;
const BRIDGE_VERSION_SETTLE_MS = 180;
const BRIDGE_PROBE_TIMEOUT_MS = 2000;

function createRequestId(prefix) {
  const random = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  return `${prefix}-${Date.now()}-${random}`;
}

function parseStoredResponse(requestId) {
  try {
    const raw = window.localStorage.getItem(`${RESPONSE_PREFIX}${requestId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function postBridgeRequest(request) {
  try { window.localStorage.setItem(REQUEST_KEY, JSON.stringify(request)); } catch {}
  window.postMessage(request, window.location.origin);
}

function versionParts(value) {
  return String(value || '')
    .trim()
    .split('.')
    .map((part) => Number.parseInt(part, 10))
    .map((part) => (Number.isFinite(part) && part >= 0 ? part : 0));
}

export function compareRaidBridgeVersions(left, right) {
  const leftParts = versionParts(left);
  const rightParts = versionParts(right);
  const length = Math.max(leftParts.length, rightParts.length, 3);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftParts[index] || 0) - (rightParts[index] || 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

function waitForBridgeMessage({ responseType, requestId, timeoutMs }) {
  return new Promise((resolve, reject) => {
    let timer = null;
    let poller = null;
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (timer) window.clearTimeout(timer);
      if (poller) window.clearInterval(poller);
      try { window.localStorage.removeItem(`${RESPONSE_PREFIX}${requestId}`); } catch {}
    };
    const accept = (payload) => {
      if (!payload || payload.type !== responseType || payload.requestId !== requestId) return false;
      cleanup();
      resolve(payload);
      return true;
    };
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      accept(event.data);
    };

    window.addEventListener('message', handleMessage);
    poller = window.setInterval(() => accept(parseStoredResponse(requestId)), BRIDGE_RESPONSE_POLL_MS);
    timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('tampermonkey_bridge_timeout'));
    }, timeoutMs);
  });
}

function collectBridgeMessages({ responseType, requestId, timeoutMs, settleMs = BRIDGE_VERSION_SETTLE_MS }) {
  return new Promise((resolve, reject) => {
    const responses = new Map();
    let timer = null;
    let settleTimer = null;
    let poller = null;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (timer) window.clearTimeout(timer);
      if (settleTimer) window.clearTimeout(settleTimer);
      if (poller) window.clearInterval(poller);
      try { window.localStorage.removeItem(`${RESPONSE_PREFIX}${requestId}`); } catch {}
    };
    const finish = () => {
      cleanup();
      const values = [...responses.values()];
      if (values.length) resolve(values);
      else reject(new Error('tampermonkey_bridge_timeout'));
    };
    const accept = (payload) => {
      if (!payload || payload.type !== responseType || payload.requestId !== requestId) return false;
      const key = [
        String(payload.version || ''),
        String(payload.tokenRevision || ''),
        String(payload.seasonNo || ''),
        String(Boolean(payload.configured)),
      ].join('|');
      responses.set(key, payload);
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(finish, settleMs);
      return true;
    };
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      accept(event.data);
    };

    window.addEventListener('message', handleMessage);
    poller = window.setInterval(() => accept(parseStoredResponse(requestId)), BRIDGE_RESPONSE_POLL_MS);
    timer = window.setTimeout(finish, timeoutMs);
  });
}

async function requestBridge(type, responseType, payload, timeoutMs) {
  const requestId = createRequestId(type.toLowerCase());
  const request = { type, requestId, ...payload };
  const responsePromise = waitForBridgeMessage({ responseType, requestId, timeoutMs });
  postBridgeRequest(request);
  return responsePromise;
}

export function extractRaidBridgeToken(value) {
  const match = String(value ?? '').match(TOKEN_PATTERN);
  return match ? match[0].toLowerCase() : '';
}

export async function probeRaidBridgeUserscript(timeoutMs = BRIDGE_PROBE_TIMEOUT_MS) {
  try {
    const requestId = createRequestId(MESSAGE_TYPES.ping.toLowerCase());
    const request = { type: MESSAGE_TYPES.ping, requestId };
    const responsePromise = collectBridgeMessages({
      responseType: MESSAGE_TYPES.pong,
      requestId,
      timeoutMs,
    });
    postBridgeRequest(request);
    const responses = await responsePromise;
    const sorted = responses.sort((left, right) => compareRaidBridgeVersions(right.version, left.version));
    const response = sorted[0];
    const detectedVersions = [...new Set(sorted.map((item) => String(item.version || '')).filter(Boolean))];
    return {
      installed: true,
      configured: Boolean(response.configured),
      version: String(response.version || ''),
      seasonNo: response.seasonNo ? Number(response.seasonNo) : null,
      tokenRevision: String(response.tokenRevision || ''),
      detectedVersions,
      multipleVersions: detectedVersions.length > 1,
    };
  } catch (error) {
    if (error instanceof Error && error.message === 'tampermonkey_bridge_timeout') {
      return {
        installed: false,
        configured: false,
        version: '',
        seasonNo: null,
        tokenRevision: '',
        detectedVersions: [],
        multipleVersions: false,
      };
    }
    throw error;
  }
}

export async function applyRaidBridgeUserscriptConfig({
  token = '',
  tokenRevision = '',
  seasonNo,
  endpoint = RAID_BRIDGE_ENDPOINT,
}) {
  const rawToken = String(token || '').trim();
  const normalizedToken = rawToken ? extractRaidBridgeToken(rawToken) : '';
  if (rawToken && !normalizedToken) throw new Error('invalid_bridge_token');
  const normalizedSeasonNo = Number(seasonNo);
  if (!Number.isInteger(normalizedSeasonNo) || normalizedSeasonNo < 1 || normalizedSeasonNo > 9999) throw new Error('invalid_season_no');

  const response = await requestBridge(MESSAGE_TYPES.config, MESSAGE_TYPES.configAck, {
    token: normalizedToken,
    tokenRevision: String(tokenRevision || '').trim(),
    seasonNo: normalizedSeasonNo,
    endpoint: String(endpoint || '').trim(),
  }, 20_000);
  if (!response.ok) throw new Error(String(response.message || 'tampermonkey_bridge_config_failed'));
  return {
    configured: Boolean(response.configured),
    version: String(response.version || ''),
    seasonNo: response.seasonNo ? Number(response.seasonNo) : null,
    tokenRevision: String(response.tokenRevision || tokenRevision || ''),
  };
}

export async function loadRaidBridgeAutoInterval() {
  const response = await requestBridge(MESSAGE_TYPES.autoStatus, MESSAGE_TYPES.autoStatusAck, {}, 10_000);
  if (!response.ok) throw new Error(String(response.message || 'tampermonkey_bridge_auto_status_failed'));
  return Number(response.autoMinutes ?? 5);
}

export async function applyRaidBridgeAutoInterval(autoMinutes) {
  const normalizedMinutes = Number(autoMinutes);
  if (!Number.isInteger(normalizedMinutes) || normalizedMinutes < 0 || normalizedMinutes > 120) {
    throw new Error('invalid_auto_interval');
  }
  const response = await requestBridge(MESSAGE_TYPES.autoConfig, MESSAGE_TYPES.autoConfigAck, {
    autoMinutes: normalizedMinutes,
  }, 10_000);
  if (!response.ok) throw new Error(String(response.message || 'tampermonkey_bridge_auto_config_failed'));
  return Number(response.autoMinutes ?? normalizedMinutes);
}

export async function collectRaidBridgeNow({ seasonNo }) {
  const normalizedSeasonNo = Number(seasonNo);
  if (!Number.isInteger(normalizedSeasonNo) || normalizedSeasonNo < 1 || normalizedSeasonNo > 9999) throw new Error('invalid_season_no');
  const response = await requestBridge(MESSAGE_TYPES.collect, MESSAGE_TYPES.collectAck, { seasonNo: normalizedSeasonNo }, 180_000);
  if (!response.ok) throw new Error(String(response.message || 'blablalink_collect_failed'));
  return {
    seasonNo: Number(response.seasonNo || normalizedSeasonNo),
    memberCount: Number(response.memberCount || 0),
    recordCount: Number(response.recordCount || 0),
    reflectedCount: Number(response.reflectedCount || 0),
    rosterComplete: Boolean(response.rosterComplete),
    rosterReflectedCount: Number(response.rosterReflectedCount || 0),
    rosterSyncError: String(response.rosterSyncError || ''),
    bosses: Array.isArray(response.bosses) ? response.bosses : [],
  };
}

export async function collectRaidBridgeRosterNow() {
  const response = await requestBridge(MESSAGE_TYPES.roster, MESSAGE_TYPES.rosterAck, {}, 90_000);
  if (!response.ok) throw new Error(String(response.message || 'blablalink_roster_collect_failed'));
  return {
    memberCount: Number(response.memberCount ?? response.member_count ?? 0),
    reflectedCount: Number(response.reflectedCount ?? response.upserted_count ?? 0),
    deactivatedCount: Number(response.deactivatedCount ?? response.deactivated_count ?? 0),
    syncedAt: String(response.syncedAt ?? response.synced_at ?? ''),
  };
}

function encodeForDoubleQuotedJavaScript(value) {
  return JSON.stringify(String(value ?? '')).slice(1, -1);
}

export function getRaidBridgeUserscriptSourceUrl(origin = window.location.origin) {
  return new URL(`${RAID_BRIDGE_USERSCRIPT_URL}?v=${RAID_BRIDGE_USERSCRIPT_VERSION}`, origin).href;
}

export function getRaidBridgeTampermonkeyInstallerUrl(origin = window.location.origin) {
  return `${TAMPERMONKEY_INSTALLER_URL}#url=${getRaidBridgeUserscriptSourceUrl(origin)}`;
}

export async function loadRaidBridgeUserscriptSource() {
  const response = await fetch(`${RAID_BRIDGE_USERSCRIPT_URL}?v=${RAID_BRIDGE_USERSCRIPT_VERSION}`, { method: 'GET', cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new Error(`userscript_load_failed_${response.status}`);
  return response.text();
}

export function configureRaidBridgeUserscriptSource(source, {
  token,
  tokenRevision = '',
  seasonNo,
  endpoint = RAID_BRIDGE_ENDPOINT,
}) {
  const normalizedToken = extractRaidBridgeToken(token);
  const normalizedEndpoint = String(endpoint || '').trim();
  const normalizedSeasonNo = Number(seasonNo);
  if (!normalizedToken) throw new Error('invalid_bridge_token');
  if (!Number.isInteger(normalizedSeasonNo) || normalizedSeasonNo < 1 || normalizedSeasonNo > 9999) throw new Error('invalid_season_no');
  return String(source || '')
    .replaceAll('__DORORAID_DEFAULT_ENDPOINT__', encodeForDoubleQuotedJavaScript(normalizedEndpoint))
    .replaceAll('__DORORAID_DEFAULT_KEY__', encodeForDoubleQuotedJavaScript(normalizedToken))
    .replaceAll('__DORORAID_DEFAULT_TOKEN_REVISION__', encodeForDoubleQuotedJavaScript(tokenRevision))
    .replaceAll('__DORORAID_DEFAULT_SEASON__', String(normalizedSeasonNo));
}

export async function copyConfiguredRaidBridgeUserscript(config) {
  const source = await loadRaidBridgeUserscriptSource();
  const configuredSource = configureRaidBridgeUserscriptSource(source, config);
  await navigator.clipboard.writeText(configuredSource);
  return configuredSource.length;
}

export function openRaidBridgeUserscriptInstaller() {
  window.open(getRaidBridgeTampermonkeyInstallerUrl(), '_blank', 'noopener,noreferrer');
}
